import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, PhoneCall } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const RANGES = ["₹50K – ₹1L", "₹1L – ₹5L", "₹5L – ₹25L", "₹25L+"];

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

export const LeadForm = ({ opportunities, selectedShare, user }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", share_interest: "", investment_range: RANGES[1], message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedShare) setForm((f) => ({ ...f, share_interest: selectedShare }));
  }, [selectedShare]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.share_interest) {
      toast.error("Please select a share of interest.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Enquiry received — our desk will reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", share_interest: "", investment_range: RANGES[1], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" className="relative overflow-hidden py-24 lg:py-32">
      <div className="absolute -right-32 top-0 h-96 w-96 rounded-full bg-[#6D28D9]/[0.06] blur-3xl" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Enquire</p>
          <h2 className="font-display mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Start the <span className="gold-text">conversation</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-500">
            Tell us what you're tracking and your indicative range. A desk advisor will respond with current pricing, availability and process details — no obligation.
          </p>
          <div className="mt-10 space-y-4 border-l-2 border-[#6D28D9]/40 pl-6">
            <p className="text-sm text-slate-700">Response within one business day</p>
            <p className="text-sm text-slate-700">Assisted, legally permitted transaction channels only</p>
            <p className="flex items-center gap-2 text-sm text-slate-700">
              <PhoneCall className="h-4 w-4 text-[#6D28D9]" /> Direct desk line shared post-enquiry
            </p>
          </div>
          <a
            data-testid="whatsapp-cta-button"
            href="https://wa.me/918169647280?text=Hi%20PRE-IPO%20EXPERT%2C%20I%27d%20like%20to%20know%20more%20about%20unlisted%20share%20opportunities."
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 flex items-center justify-center gap-3 border border-emerald-600/40 bg-emerald-600/[0.08] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700 transition-all duration-300 hover:bg-emerald-600/20 hover:text-emerald-800"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Chat on WhatsApp
          </a>
          <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Desk line · +91 81696 47280 · replies within business hours
          </p>
        </motion.div>

        <motion.form
          data-testid="lead-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="border border-slate-200 bg-[#F6F4FD]/70 p-8 backdrop-blur lg:col-span-7 lg:p-10"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <input data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
            <input data-testid="lead-form-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Phone (+91…)" type="tel" className={inputCls} />
            <input data-testid="lead-form-email-input" required value={form.email} onChange={set("email")} placeholder="Email address" type="email" className={`${inputCls} sm:col-span-2`} />
            <select data-testid="lead-form-share-select" value={form.share_interest} onChange={set("share_interest")} className={`${inputCls} appearance-none`}>
              <option value="" disabled>Share of interest</option>
              {opportunities.map((o) => (
                <option key={o.slug} value={o.name}>{o.name}</option>
              ))}
              <option value="General Enquiry">General Enquiry</option>
            </select>
            <select data-testid="lead-form-investment-range-select" value={form.investment_range} onChange={set("investment_range")} className={`${inputCls} appearance-none`}>
              {RANGES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <textarea
              data-testid="lead-form-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder="Anything specific we should know? (optional)"
              rows={3}
              className={`${inputCls} resize-none sm:col-span-2`}
            />
          </div>
          <button
            data-testid="lead-form-submit-button"
            type="submit"
            disabled={loading}
            className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-all duration-300 hover:bg-[#5B21B6] disabled:opacity-50"
          >
            {loading ? "Sending…" : "Submit Enquiry"}
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="mt-4 text-center font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Information purposes only · Not investment advice
          </p>
        </motion.form>
      </div>
    </section>
  );
};
